import { IconName } from '@lyse-as/formds-icons';
import { Component, h, Host, Prop } from '@stencil/core';

import store from '@/store';

export type FdsLinkTarget = '_self' | '_blank';
export type FdsLinkAriaCurrent = 'page' | 'false';
export type FdsLinkAriaLabel = string;
export type FdsLinkAriaProps = {
  href: string;
  target: FdsLinkTarget;
  'aria-current'?: FdsLinkAriaCurrent;
  'aria-label'?: FdsLinkAriaLabel;
};

@Component({
  tag: 'fds-link',
  styleUrl: 'styles/link.scss',
  shadow: true,
})
export class Link {
  /**
   * The URL that the hyperlink points to
   */
  @Prop() href!: string;

  /**
   * Specifies where to open the linked document
   */
  @Prop() target: FdsLinkTarget = '_self';

  /**
   * Indicates whether the link is white or not
   */
  @Prop() white?: boolean;

  /**
   * The a11y label for the link
   */
  // eslint-disable-next-line @stencil-community/reserved-member-names
  @Prop() ariaLabel: FdsLinkAriaLabel;

  /**
   * Indicates whether the link is the current page or not (used in breadcrumb)
   */
  // eslint-disable-next-line @stencil-community/reserved-member-names
  @Prop() ariaCurrent: FdsLinkAriaCurrent;

  /**
   * Set which icon to render to the right-hand side of the input text
   */
  @Prop() icon: IconName;

  /**
   * Navigate within the application without refreshing the page
   */
  @Prop() preventReload?: boolean;

  private handleClick = (event: MouseEvent) => {
    if (this.preventReload) {
      event.preventDefault();
      window.history.pushState({}, '', this.href);
      window.dispatchEvent(new Event('popstate'));
    }
  };

  render() {
    const { href, target, ariaLabel, ariaCurrent, white, icon, handleClick } = this;

    return (
      <Host class={`fds-link ${store.mode ?? ''}`}>
        <a
          class={`fds-link__link ${white ? 'fds-link__link--white' : ''}`}
          href={href}
          target={target}
          aria-label={ariaLabel}
          aria-current={ariaCurrent}
          tabIndex={0}
          onClick={handleClick}
        >
          <slot></slot>
          {icon ? (
            <span class="fds-link__icon">
              <fds-icon name={icon ?? 'launch'} />
            </span>
          ) : (
            ''
          )}
        </a>
      </Host>
    );
  }
}
